import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationHostTypeService, ReferenceDataService } from 'rome-library';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserContext } from '../models/user-context.model';
import { AuthorizationService } from './authorization.service';
import { TokenStore } from './token.store';
import { UserNotificationService } from './user-notification.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly authorizationService: AuthorizationService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly router: Router,
    private readonly tokenStore: TokenStore,
    private readonly applicationHostTypeService: ApplicationHostTypeService,
    private readonly userService: UserService,
    private readonly userNotificationService: UserNotificationService
  ) {}

  public login(username: string, password: string, otpCode?: string): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    const applicationVersion = this.referenceDataService.getApplicationCurrentVersion();
    const loginData = `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(
      password
    )}`;
    let httpParams = new HttpParams().set('appVersion', applicationVersion);

    if (otpCode) {
      httpParams = httpParams.set('twoFactorAuthenticationOtpCode', otpCode);
    }

    const options = {
      headers,
      params: httpParams
    };

    return this.httpClient.post('/token', loginData, options).pipe(
      map((responseData) => new UserContext().deserialize(responseData)),
      tap((userContext) => {
        this.tokenStore.setUserContext(userContext);
        this.userNotificationService.initialize();
      }),
      switchMap((userContext) => {
        return this.userService
          .setUserData(userContext, password)
          .pipe(
            catchError((_) =>
              throwError(
                () =>
                  new Error('Unexpexted glitch while retrieving user data, cookies information and application version')
              )
            )
          );
      })
    );
  }

  public logout(redirectState: string): Observable<any> {
    return this.authorizationService.resetUserAccessToken().pipe(
      catchError((_) => of(undefined)),
      tap((_) => {
        this.resetAuthenticatedUser();
        void this.router.navigate(['/', this.applicationHostTypeService.getCode(), redirectState]);
      })
    );
  }

  public logoutWithoutRedirect(): Observable<any> {
    return this.authorizationService.resetUserAccessToken().pipe(
      catchError((_) => of(undefined)),
      tap((_) => {
        this.resetAuthenticatedUser();
      })
    );
  }

  public resetAuthenticatedUser(): void {
    if (this.userService.getUser()) {
      this.userService.getUser().IsAuthenticated = false;
    }
    this.tokenStore.setUserContext(new UserContext());
    this.tokenStore.setSessionToken(undefined);
  }
}
